import { useQuery } from '@tanstack/react-query';
import {
  LinkIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { StatCard, Card, CardBody, CardHeader, CardTitle } from '@/components/ui';
import { StatusBadge, Spinner } from '@/components/ui';
import { paymentLinkApi, transactionApi } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { format } from 'date-fns';

export function DashboardPage() {
  const user = useAuthStore((state) => state.user);

  const { data: linkStats, isLoading: linksLoading } = useQuery({
    queryKey: ['payment-link-stats'],
    queryFn: async () => {
      const response = await paymentLinkApi.getStatistics();
      return response.data.data;
    },
  });

  const { data: recentLinks, isLoading: recentLoading } = useQuery({
    queryKey: ['recent-payment-links'],
    queryFn: async () => {
      const response = await paymentLinkApi.list({ limit: 5 });
      return response.data.data;
    },
  });

  const { data: recentTransactions } = useQuery({
    queryKey: ['recent-transactions'],
    queryFn: async () => {
      const response = await transactionApi.list({ limit: 5 });
      return response.data.data;
    },
    enabled: user?.role === 'ADMIN' || user?.role === 'FINANCE',
  });

  const formatCurrency = (amount: number, currency = 'PHP') => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  if (linksLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">
          Welcome back, {user?.firstName}! Here's an overview of your payment links.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Links"
          value={linkStats?.totalLinks || 0}
          icon={<LinkIcon className="w-6 h-6" />}
        />
        <StatCard
          title="Active Links"
          value={linkStats?.activeLinks || 0}
          icon={<ClockIcon className="w-6 h-6" />}
        />
        <StatCard
          title="Paid Links"
          value={linkStats?.paidLinks || 0}
          icon={<CheckCircleIcon className="w-6 h-6" />}
        />
        <StatCard
          title="Total Collected"
          value={formatCurrency(linkStats?.paidAmount || 0)}
          icon={<CurrencyDollarIcon className="w-6 h-6" />}
        />
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent payment links */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Payment Links</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            {recentLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recentLinks?.map((link: {
                  id: string;
                  title: string;
                  customerName?: string;
                  amount: number;
                  currency: string;
                  status: string;
                  createdAt: string;
                }) => (
                  <div
                    key={link.id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {link.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {link.customerName || 'No customer'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(link.amount, link.currency)}
                        </span>
                        <StatusBadge status={link.status} />
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Created {format(new Date(link.createdAt), 'MMM d, yyyy')}
                    </p>
                  </div>
                ))}
                {recentLinks?.length === 0 && (
                  <div className="px-6 py-8 text-center text-sm text-gray-500">
                    No payment links yet
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Recent transactions - only for Admin/Finance */}
        {(user?.role === 'ADMIN' || user?.role === 'FINANCE') && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <div className="divide-y divide-gray-100">
                {recentTransactions?.map((tx: {
                  id: string;
                  transactionRef: string;
                  paymentLink?: { title: string };
                  amount: number;
                  currency: string;
                  status: string;
                  createdAt: string;
                }) => (
                  <div
                    key={tx.id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {tx.transactionRef}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {tx.paymentLink?.title || 'Unknown link'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(tx.amount, tx.currency)}
                        </span>
                        <StatusBadge status={tx.status} />
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      {format(new Date(tx.createdAt), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                ))}
                {recentTransactions?.length === 0 && (
                  <div className="px-6 py-8 text-center text-sm text-gray-500">
                    No transactions yet
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
}
